import { backend, toast, useApp } from '../data/store';
import { askText, choose, confirm } from '../ui/controls';

const PLAN = { free: 'Free', basic: 'Basic', pro: 'Pro' } as const;

/**
 * Leaving the masjid (Settings › Team, "Leave"). The owner hands the masjid to someone first. The last person gets a
 * clear warning, then types the masjid's name, because leaving closes the whole account. Returns true if they left.
 */
export async function leaveMasjid(): Promise<boolean> {
  const { ws, session } = useApp.getState();
  if (!ws || !session || !backend.leaveMasjid) return false;
  const name = ws.masjid.name;
  const me = ws.members.find((m) => m.id === session.userId);
  const others = ws.members.filter((m) => m.id !== session.userId);
  let newOwner: string | null = null;

  if (!others.length) {
    const go = await confirm('Close your NoorView account?', {
      body: `You’re the last person on ${name}’s team. If you leave, ${name}’s whole NoorView account is deleted: its screens, designs, schedules, timetable and settings. The ${PLAN[ws.masjid.plan]} plan stops and your screens stop updating. This can’t be undone. To use NoorView again, you’ll need to register again.`,
      ok: 'Continue', danger: true,
    });
    if (!go) return false;
    const typed = await askText(`Type “${name}” to close the account`, { label: name, value: '', ok: 'Close account' });
    if (typed == null) return false;
    if (typed.trim().toLowerCase() !== name.trim().toLowerCase()) { toast('That name doesn’t match, so nothing was deleted.', { bad: true }); return false; }
  } else {
    if (me?.role === 'owner') {
      newOwner = await choose(`Who will own ${name}?`, {
        body: 'You’re the owner, so choose who takes over before you leave. They’ll look after the plan, settings and team.',
        choices: [...others.map((m) => ({ value: m.id, label: `${m.name || m.email} · ${m.role === 'admin' ? 'Admin' : 'Editor'}`, kind: 'plain' as const })), { value: '', label: 'Cancel', kind: 'plain' as const }],
      });
      if (!newOwner) return false;
    }
    const heir = others.find((m) => m.id === newOwner);
    const ok = await confirm(`Leave ${name}?`, {
      body: `${heir ? `${heir.name || heir.email} will be the owner. ` : ''}You won’t be able to open ${name} in NoorView any more, unless someone adds you again.`,
      ok: 'Leave', danger: true,
    });
    if (!ok) return false;
  }

  try {
    const r = await backend.leaveMasjid(newOwner);
    useApp.getState().setSession(null);
    toast(r.closed ? `${name}’s NoorView account has been closed.` : `You’ve left ${name}.`);
    return true;
  } catch (e) {
    toast(e instanceof Error ? e.message : 'Couldn’t leave. Please try again.', { bad: true });
    return false;
  }
}
